import { err, ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import {
  targetedCronTriggerNodeModel,
  targetWorkflowIdFromContext,
  TRIGGER_CRON_NOW_ACTIVE_TITLE,
  workflowModelFromContext,
} from './workflow-toolbar-action.helpers';
import { shouldUseDraftTestRun } from './workflow-draft-test-run';

export class WorkflowToolbarTriggerCronNowAction extends WorkflowEditorToolbarActionBase<
  { t: string; message?: string }[]
> {
  title = 'Trigger now';
  override description = TRIGGER_CRON_NOW_ACTIVE_TITLE;
  group = 'Workflow';
  icon = 'play';

  constructor(
    private readonly workflowAccess: {
      getWorkflow: () => TenantedClient['resource']['workflow'];
    },
    private readonly onTriggerStarted?: () => void
  ) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    return targetedCronTriggerNodeModel(context) !== null;
  }

  override async execute(
    _params: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const cron = targetedCronTriggerNodeModel(context);
    if (cron === null) {
      return ok({});
    }

    if (shouldUseDraftTestRun(context)) {
      const model = workflowModelFromContext(context);
      if (model === null) {
        toast.error('Could not start draft test');
        return err([{ t: 'error', message: 'Workflow model not in scope' }]);
      }
      const draftResult = await model.startDraftTestRun();
      if (draftResult.success === false) {
        toast.error(draftResult.message);
        return err([{ t: 'error', message: draftResult.message }]);
      }
      toast.success('Draft test started — check the Runs panel');
      this.onTriggerStarted?.();
      return ok({});
    }

    const workflowId = targetWorkflowIdFromContext(context);
    if (workflowId === null) {
      toast.error('Could not tell which workflow to run');
      return err([{ t: 'error', message: 'Workflow model not in scope' }]);
    }

    const res = await this.workflowAccess.getWorkflow().triggerNow({
      triggerId: cron.triggerId,
      // This workflow only. The schedule may drive several, and the button is
      // on one graph.
      workflowIds: [workflowId],
    });
    if (res.isErr()) {
      toast.error('Could not trigger cron schedule');
      return err([{ t: 'error', message: JSON.stringify(res.error) }]);
    }

    if (res.value.runs.length === 0) {
      toast.success(
        "Trigger fired, but this workflow didn't run — check that it's active and still bound to the trigger."
      );
      return ok({});
    }

    toast.success('Cron triggered — check the Runs panel');
    this.onTriggerStarted?.();
    return ok({});
  }
}
